// ...existing contents of src/tabs/MutualFundRecommender.vue remain exactly the same here...
<script setup>
import { ref, computed } from 'vue';
import { useUserStore } from '@/store/user-store';
import MutualFundQuestionnaire from '@/components/mutualfund/MutualFundQuestionnaire.vue';
import MutualFundReview from '@/components/mutualfund/MutualFundReview.vue';
import MutualFundResult from '@/components/mutualfund/MutualFundResult.vue';

const props = defineProps({
  sipAmount: {
    type: Number,
    default: null
  },
  saveToPortfolio: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['allocation-complete']);

const store = useUserStore();

const step = ref(1); // 1=questions, 2=review, 3=result
const answers = ref({});
const isLoading = ref(false);
const apiError = ref('');
const recommendation = ref(null);

const canGoBack = computed(() => step.value > 1 && !isLoading.value);

const handleQuestionnaireComplete = (payload) => {
  answers.value = payload;
  step.value = 2;
};

const handleEditAnswers = () => {
  step.value = 1;
};

const handleSubmit = async () => {
  apiError.value = '';
  isLoading.value = true;
  try {
    const resp = await store.getMutualFundRecommender(answers.value);
    recommendation.value = resp;
    step.value = 3;

    // If saveToPortfolio prop is true and sipAmount is provided, emit allocation complete event
    if (props.saveToPortfolio && props.sipAmount && resp && resp.funds) {
      const allocationData = {
        monthlyAmount: props.sipAmount,
        funds: resp.funds.map(fund => ({
          fundName: fund.fundName || fund.name,
          category: fund.category || fund.assetClass,
          sipAmount: (props.sipAmount * (fund.percentage / 100)),
          percentage: fund.percentage,
          risk: fund.risk || 'Medium',
          expectedReturn: fund.expectedReturn || null
        })),
        preferences: answers.value, // User's questionnaire answers
        questionnaireAnswers: answers.value, // Same data, for compatibility
        sipTier: 3, // Premium tier
        tierRange: '15000-50000'
      };

      console.log('MutualFundRecommender - Emitting allocation-complete event with data:', allocationData);
      console.log('MutualFundRecommender - Questionnaire answers:', answers.value);
      // Emit to parent component (SIPPortfolioTab) which will handle saving to DB
      emit('allocation-complete', allocationData);
    }
  } catch (e) {
    console.error('Error fetching mutual fund recommendation', e);
    apiError.value = 'Unable to fetch recommendation right now. Please try again.';
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="mf-recommender-page">
    <div class="page-body">
      <div class="card shell">
        <div class="shell-header">
          <div class="steps">
            <div :class="['step-pill', { active: step === 1 }]">1. Questionnaire</div>
            <div :class="['step-pill', { active: step === 2 }]">2. Review</div>
            <div :class="['step-pill', { active: step === 3 }]">3. Recommendation</div>
          </div>
          <button v-if="canGoBack" class="back-link" @click="step === 3 ? (step = 2) : (step = 1)">
            ← Back
          </button>
        </div>

        <div v-if="apiError" class="error-banner">{{ apiError }}</div>

        <div v-if="step === 1" class="shell-content">
          <MutualFundQuestionnaire
            :initial-answers="answers"
            :disabled="isLoading"
            @complete="handleQuestionnaireComplete"
          />
        </div>

        <div v-else-if="step === 2" class="shell-content">
          <MutualFundReview
            :answers="answers"
            :disabled="isLoading"
            @edit="handleEditAnswers"
            @submit="handleSubmit"
          />
        </div>

        <div v-else-if="step === 3" class="shell-content">
          <MutualFundResult
            :answers="answers"
            :result="recommendation"
            :loading="isLoading"
            @edit="handleEditAnswers"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mf-recommender-page {
  padding: 24px;
}

.page-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card.shell {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 20px 24px;
}

.shell-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.steps {
  display: flex;
  gap: 8px;
}

.step-pill {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f1f3f5;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
}

.step-pill.active {
  background: var(--an-primary-light, #e3f2fd);
  color: var(--an-primary, #1976d2);
}

.back-link {
  border: none;
  background: transparent;
  color: var(--an-primary, #1976d2);
  font-size: 0.8rem;
  cursor: pointer;
}

.shell-content {
  margin-top: 8px;
}

.error-banner {
  background: #ffe3e3;
  border-radius: 6px;
  padding: 8px 10px;
  color: #c92a2a;
  font-size: 0.82rem;
  margin-bottom: 12px;
}

@media (max-width: 768px) {
  .mf-recommender-page {
    padding: 16px;
  }

  .shell-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
